import {
	WebAPIBarGraphChartSlicerData,
	WebAPIDataEntryList,
} from '../../../../globals/webApiTypes/elements/WebAPICBS';
import {
	BarByCategory,
	BarByTopic,
	ValuesByComparingDimension,
} from '../barChart.types';
import {
	matchBarChartDimensions,
	prepareBarChartYAxisDimension,
	prepareBarChartComparingDimension,
} from './prepareBarChartDimension';
import { sortByOrder } from './sortByOrder';

export const getDataPropertiesFromBars = (
	bars: Array<BarByCategory> | Array<BarByTopic>,
) => {
	if (bars.length <= 0) {
		return {};
	}

	const { min, max } = bars.reduce(
		({ min, max }, { value }) => {
			return {
				min: Math.min(value, min),
				max: Math.max(value, max),
			};
		},
		{
			min: Infinity,
			max: -Infinity,
		},
	);

	const middle = (min + max) / 2;

	return { min, max, middle };
};

const getDataListForTopic = ({
	dataEntryList,
	yAxisDimension,
	categoryKeys,
	labelAliases,
	comparingTopicKey,
	selectedCategories,
	selectedTopics,
}: Omit<
	ArrangeValuesByComparingDimensionProps,
	'comparingDimension' | 'topicKeys'
> & { comparingTopicKey: string }) => {
	if (yAxisDimension === 'DIMENSION_TOPIC') {
		const dataEntry = dataEntryList.find((dataEntry) => {
			const entryCategories = dataEntry.categories;
			const value = dataEntry.values[comparingTopicKey];
			return (
				value != null &&
				matchBarChartDimensions({
					matchingDimensions: categoryKeys,
					entryCategories,
				})
			);
		});

		if (!dataEntry) {
			return [];
		}

		const value = dataEntry.values[comparingTopicKey];
		const topicData = selectedTopics[comparingTopicKey];

		return [
			{
				type: 'TOPIC',
				...prepareBarChartYAxisDimension(labelAliases)(topicData),
				yAxisDimension,
				value,
			},
		] as Array<BarByTopic>;
	}

	const categoryKeysForDimension = categoryKeys[yAxisDimension];
	const otherDimensions = { ...categoryKeys };
	delete otherDimensions[yAxisDimension];

	const dataList = categoryKeysForDimension.reduce(
		(memo, categoryKeyForDimension) => {
			const dataEntry = dataEntryList.find((dataEntry) => {
				const entryCategories = dataEntry.categories;

				const value = dataEntry.values[comparingTopicKey];
				return (
					value != null &&
					entryCategories[yAxisDimension] === categoryKeyForDimension &&
					matchBarChartDimensions({
						matchingDimensions: otherDimensions,
						entryCategories,
					})
				);
			});

			if (!dataEntry) {
				return memo;
			}

			const categoryData =
				selectedCategories[yAxisDimension][categoryKeyForDimension];

			const value = dataEntry.values[comparingTopicKey];

			const bar = {
				type: 'CATEGORY' as BarByCategory['type'],
				...prepareBarChartYAxisDimension(labelAliases)(categoryData),
				yAxisDimension,
				value,
			};

			return [...memo, bar];
		},
		[] as Array<BarByCategory>,
	);

	return sortByOrder<Array<BarByCategory>>(dataList);
};

const getDataListForEqualDimensions = ({
	comparingDimension,
	yAxisDimension,
	topicKeys,
	categoryKeys,
	dataEntryList,
	selectedCategories,
	selectedTopics,
	labelAliases,
}: ArrangeValuesByComparingDimensionProps) => {
	if (yAxisDimension === 'DIMENSION_TOPIC') {
		const dataList = topicKeys.reduce((memo, topicKey) => {
			const dataEntry = dataEntryList.find((dataEntry) => {
				const entryCategories = dataEntry.categories;

				const value = dataEntry.values[topicKey];
				return (
					value != null &&
					matchBarChartDimensions({
						matchingDimensions: categoryKeys,
						entryCategories,
					})
				);
			});

			if (!dataEntry) {
				return memo;
			}

			const value = dataEntry.values[topicKey];
			const topicData = selectedTopics[topicKey];

			return [
				...memo,
				{
					type: 'TOPIC' as BarByTopic['type'],
					...prepareBarChartYAxisDimension(labelAliases)(topicData),
					yAxisDimension,
					value,
				},
			];
		}, [] as Array<BarByTopic>);

		return sortByOrder<Array<BarByTopic>>(dataList);
	}

	const otherDimensions = { ...categoryKeys };
	delete otherDimensions[yAxisDimension];

	const topicKey = topicKeys?.[0];

	const categoryKeysForDimension = categoryKeys[yAxisDimension];

	const dataList = categoryKeysForDimension.reduce(
		(memo, categoryKeyForDimension) => {
			const dataEntry = dataEntryList.find((dataEntry) => {
				const entryCategories = dataEntry.categories;

				const value = dataEntry.values[topicKey];
				return (
					value != null &&
					entryCategories[yAxisDimension] === categoryKeyForDimension &&
					matchBarChartDimensions({
						matchingDimensions: otherDimensions,
						entryCategories,
					})
				);
			});

			if (!dataEntry) {
				return memo;
			}

			const categoryData =
				selectedCategories[yAxisDimension][categoryKeyForDimension];

			const value = dataEntry.values[topicKey];

			const bar = {
				type: 'CATEGORY' as BarByCategory['type'],
				...prepareBarChartYAxisDimension(labelAliases)(categoryData),
				comparingDimension,
				value,
			};

			return [...memo, bar];
		},
		[] as Array<BarByCategory>,
	);
	return sortByOrder<Array<BarByCategory>>(dataList);
};

const getDataListForCategory = ({
	comparingDimension,
	yAxisDimension,
	comparingCategoryKey,
	topicKeys,
	categoryKeys,
	dataEntryList,
	selectedCategories,
	selectedTopics,
	labelAliases,
}: ArrangeValuesByComparingDimensionProps & {
	comparingCategoryKey: string;
}) => {
	if (yAxisDimension === 'DIMENSION_TOPIC') {
		const dataList = topicKeys.reduce((memo, topicKey) => {
			const dataEntry = dataEntryList.find((dataEntry) => {
				const entryCategories = dataEntry.categories;

				const value = dataEntry.values[topicKey];
				return (
					value != null &&
					entryCategories[comparingDimension] === comparingCategoryKey &&
					matchBarChartDimensions({
						matchingDimensions: categoryKeys,
						entryCategories,
					})
				);
			});

			if (!dataEntry) {
				return memo;
			}

			const value = dataEntry.values[topicKey];
			const topicData = selectedTopics[topicKey];

			return [
				...memo,
				{
					type: 'TOPIC' as BarByTopic['type'],
					...prepareBarChartYAxisDimension(labelAliases)(topicData),
					yAxisDimension,
					value,
				},
			];
		}, [] as Array<BarByTopic>);

		return sortByOrder<Array<BarByTopic>>(dataList);
	}

	const otherDimensions = { ...categoryKeys };
	delete otherDimensions[yAxisDimension];

	const topicKey = topicKeys?.[0];

	const categoryKeysForDimension = categoryKeys[yAxisDimension];

	const dataList = categoryKeysForDimension.reduce(
		(memo, categoryKeyForDimension) => {
			const dataEntry = dataEntryList.find((dataEntry) => {
				const entryCategories = dataEntry.categories;

				const value = dataEntry.values[topicKey];
				return (
					value != null &&
					entryCategories[comparingDimension] === comparingCategoryKey &&
					entryCategories[yAxisDimension] === categoryKeyForDimension &&
					matchBarChartDimensions({
						matchingDimensions: otherDimensions,
						entryCategories,
					})
				);
			});

			if (!dataEntry) {
				return memo;
			}

			const categoryData =
				selectedCategories[yAxisDimension][categoryKeyForDimension];

			const value = dataEntry.values[topicKey];

			const bar = {
				type: 'CATEGORY' as BarByCategory['type'],
				...prepareBarChartYAxisDimension(labelAliases)(categoryData),
				comparingDimension,
				value,
			};

			return [...memo, bar];
		},
		[] as Array<BarByCategory>,
	);
	return sortByOrder<Array<BarByCategory>>(dataList);
};

type ArrangeValuesByComparingDimensionProps = {
	dataEntryList: WebAPIDataEntryList;
} & Pick<
	WebAPIBarGraphChartSlicerData,
	| 'comparingDimension'
	| 'yAxisDimension'
	| 'topicKeys'
	| 'categoryKeys'
	| 'selectedTopics'
	| 'selectedCategories'
	| 'labelAliases'
>;

export const arrangeValuesByComparingDimension = ({
	dataEntryList,
	comparingDimension,
	yAxisDimension,
	topicKeys,
	categoryKeys,
	selectedTopics,
	selectedCategories,
	labelAliases,
}: ArrangeValuesByComparingDimensionProps): ValuesByComparingDimension => {
	if (comparingDimension === yAxisDimension) {
		const dataList = getDataListForEqualDimensions({
			comparingDimension,
			yAxisDimension,
			topicKeys,
			categoryKeys,
			dataEntryList,
			selectedCategories,
			selectedTopics,
			labelAliases,
		});

		if (dataList.length <= 0) return [];

		return [
			{
				dataList,
				...getDataPropertiesFromBars(dataList),
			},
		] as ValuesByComparingDimension;
	}

	if (comparingDimension === 'DIMENSION_TOPIC') {
		const valuesByDimension = topicKeys.reduce((memo, topicKey) => {
			const topic = selectedTopics[topicKey];
			const dataList = getDataListForTopic({
				yAxisDimension,
				categoryKeys,
				dataEntryList,
				selectedTopics,
				selectedCategories,
				labelAliases,
				comparingTopicKey: topicKey,
			});

			if (dataList.length > 0) {
				return [
					...memo,
					{
						type: 'TOPIC' as ValuesByComparingDimension[number]['type'],
						...prepareBarChartComparingDimension(labelAliases)(topic),
						dataList,
						...getDataPropertiesFromBars(dataList),
					},
				];
			}

			return memo;
		}, [] as ValuesByComparingDimension);

		return sortByOrder<ValuesByComparingDimension>(valuesByDimension, {
			descendingOrder: true,
		});
	}

	const yAxisCategoryKeys = categoryKeys[comparingDimension];

	const valuesByDimension = yAxisCategoryKeys.reduce((memo, categoryKey) => {
		const category = selectedCategories[comparingDimension][categoryKey];
		const dataList = getDataListForCategory({
			comparingDimension,
			yAxisDimension,
			topicKeys,
			categoryKeys,
			dataEntryList,
			selectedCategories,
			selectedTopics,
			labelAliases,
			comparingCategoryKey: categoryKey,
		});

		if (dataList.length > 0) {
			return [
				...memo,
				{
					type: 'CATEGORY' as ValuesByComparingDimension[number]['type'],
					...prepareBarChartComparingDimension(labelAliases)(category),
					dataList,
					...getDataPropertiesFromBars(dataList),
				},
			];
		}

		return memo;
	}, [] as ValuesByComparingDimension);

	return sortByOrder(valuesByDimension, {
		descendingOrder: true,
	});
};
